import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.customer_engagement_api';

const ENDPOINTS = {
  getSegments: `${BASE}.get_customer_segments`,
  getSegmentCustomers: `${BASE}.get_segment_customers`,
  sendCommunication: `${BASE}.send_communication`,
  getHistory: `${BASE}.get_communication_history`,
};

const extract = (res) =>
  res.data?.message && typeof res.data.message === 'object'
    ? res.data.message
    : res.data?.message || res.data;

const extractErr = (err) => {
  const d = err.response?.data;
  if (d?._server_messages) {
    try {
      const msgs = JSON.parse(d._server_messages);
      const first = Array.isArray(msgs) ? msgs[0] : null;
      if (first) {
        const p = typeof first === 'string' ? JSON.parse(first) : first;
        if (p?.message) return String(p.message).replace(/<[^>]*>/g, '').trim();
      }
    } catch { /* fall through */ }
  }
  return d?.message || err.message || 'An error occurred';
};

export const getCustomerSegments = createAsyncThunk('engagement/segments', async (_, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getSegments);
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getSegmentCustomers = createAsyncThunk('engagement/segmentCustomers', async ({ segment, limit = 200 }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getSegmentCustomers, { params: { segment, limit } });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const sendCommunication = createAsyncThunk('engagement/send', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.sendCommunication, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Communication sent', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getCommunicationHistory = createAsyncThunk('engagement/history', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getHistory, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

const customerEngagementSlice = createSlice({
  name: 'customerEngagement',
  initialState: {
    segments: {},
    segmentCustomers: [],
    history: [],
    total: 0,
    loading: false,
    sending: false,
    error: null,
  },
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(getCustomerSegments.pending, loading)
      .addCase(getCustomerSegments.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = action.payload?.segments || {};
      })
      .addCase(getCustomerSegments.rejected, failed)
      .addCase(getSegmentCustomers.pending, loading)
      .addCase(getSegmentCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.segmentCustomers = action.payload?.customers || [];
      })
      .addCase(getSegmentCustomers.rejected, failed)
      .addCase(sendCommunication.pending, (state) => { state.sending = true; state.error = null; })
      .addCase(sendCommunication.fulfilled, (state) => { state.sending = false; })
      .addCase(sendCommunication.rejected, (state, action) => { state.sending = false; state.error = action.payload; })
      .addCase(getCommunicationHistory.pending, loading)
      .addCase(getCommunicationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload?.records || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(getCommunicationHistory.rejected, failed);
  },
});

export const { clearError } = customerEngagementSlice.actions;
export default customerEngagementSlice.reducer;
